import moment from "moment";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useLocation } from "react-router";
import { Button, Skeleton } from "@mui/material";
import NoDataCard from "../no-data-card";
import { useDispatch } from "react-redux";
import { setOperationTitle } from "../../store/reducers/operation";
import { dateFormater } from "../../bmr/utils/common/utils";

const PendingTaskTile = ({
  task = [],
  isClosed,
  loadMoreData,
  maxPages,
  page,
  loading,
  isDashboard,
  showBoxShadow = false
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const handleTaskView = async (taskDetails) => {
    const batchNo =  taskDetails?.finalBatchNo ? '?batch='+taskDetails?.finalBatchNo :'';
    const prodDetailsId = taskDetails?.phase?.product_details?.id;
    const phaseId = taskDetails?.patient?.id;
    const operationTitle =
      taskDetails?.change_request_reason_value?.[0]?.slug || "";

    if (taskDetails?.status == "0") {
      await dispatch(setOperationTitle(operationTitle));
    }
    // Check if we're currently in BMR section
    const isBMRSection = location.pathname.includes("/bmr/");
   if (operationTitle == "change-terminate-cycle-status") {
      navigate(`/bmr/manufacturer_view/${taskDetails?.qrCode?.id}/${taskDetails?.feasibility_assessments_id}${batchNo}`);
    }else if (operationTitle == "change-scheduled-aphresis-date-range") {
      navigate(`/bmr/feasibility-assessment/subjects/details/${phaseId}${batchNo}`);
    } else if (
      ["change-financial-feasibility", "change-clinical-feasibility"].includes(
        operationTitle
      )
    ) {
      navigate(`/bmr/feasibility-assessment/subjects/details/${phaseId}${batchNo}`);
    } else {
      // Navigate based on current section
      if (isBMRSection) {
        navigate(
          `/bmr/products/product-details/${prodDetailsId}/patient-details/${phaseId}${batchNo}`
        );
      } else {
        navigate(
          `/products/product-details/${prodDetailsId}/patient-details/${phaseId}${batchNo}`
        );
      }
    }
  };

  const getTaskColorAndDate = (createdAt, isClosed) => {
    const today = moment().startOf("day");
    const createdDate = moment(createdAt).startOf("day");
    const diffInDays = today.diff(createdDate, "days");

    if (isClosed) {
      return {
        taskColor: "#00B278",
        dateText:
          diffInDays === 0
            ? "Today"
            : `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
      };
    }

    if (diffInDays === 0) {
      return {
        taskColor: "#3b82f6",
        dateText: "Today",
      };
    } else if (diffInDays <= 3) {
      return {
        taskColor: "#f97316",
        dateText: `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
      };
    }

    return {
      taskColor: "#ef4444",
      dateText: `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
    };
  };

  return (
    <>
      {loading ? (
          <Skeleton height={"150px"} />
       ) : !loading && task?.length === 0 ? (
        <div className="col-start-4 col-end-13">
          <NoDataCard heading="No Data to Display" />
        </div>
      ) : (
        <InfiniteScroll
          dataLength={task?.length || 0}
          next={loadMoreData}
          hasMore={page <= maxPages}
        >
          {task.map((op, index) => {
            const { taskColor, dateText } = getTaskColorAndDate(
              op?.createdAt,
              isClosed
            );

            return (
              <>
                {loading ? (
                  <Skeleton height={"150px"} />
                ) :!loading && !task?.length > 0 ? (
                  <div className="col-start-4 col-end-13">
                    <NoDataCard heading="No Data to Display" />
                  </div>
                ) : (
                  <div
                    key={index}
                    className="bg-white flex flex-col md:flex-row justify-between rounded-md p-2 pt-2 border-l-4 mb-2 p-3"
                    style={{
                      borderLeftWidth: "2px",
                      borderLeftColor: isClosed ? "#00B278" : taskColor,
                      boxShadow: showBoxShadow ? "0px 2px 8px rgba(0, 0, 0, 0.1)" : "none"
                    }}
                  >
                    <div className="task-start w-full md:w-[50%] pr-0 md:pr-4">
                      <div>
                        <span className="font-bold text-sm md:text-base block mb-1 capitalize">
                          {op?.phase?.product_details?.name} {op?.phase?.title}
                        </span>
                        <div className="text-sm text-gray-400 pb-1">
                          In {op?.changeRequestReason?.title}{" "}
                          {op?.creator?.name} wants to{" "}
                          {Array.isArray(op?.change_request_reason_value)
                            ? op.change_request_reason_value
                              .map((reas) => reas.title)
                              .join(", ")
                            : "--"}{" "}
                          for {op?.patient?.first_name} {op?.patient?.last_name}
                        </div>
                        <div>
                          {op.createdAt ? (
                            <>
                              {" "}
                              {dateFormater(op.createdAt)}{" "}
                              <span
                                style={{
                                  color: isClosed ? "#00B278" : taskColor,
                                }}
                              >
                                {" "}
                                ({dateText})
                              </span>{" "}
                            </>
                          ) : (
                            "--"
                          )}
                        </div>
                      </div>
                    </div>

                    <div
                      className={
                        !isClosed
                          ? "mr-5 justify-end w-full md:w-[50%] flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5 mt-3 sm:mt-0"
                          : "w-full md:w-[50%] flex flex-col sm:flex-row justify-around items-start sm:items-center gap-3 sm:gap-5 mt-3 sm:mt-0"
                      }
                    >
                      {isClosed && (
                        <>
                          <div>
                            <p className="text-gray-500">Close By</p>
                            <p className="font-bold text-sm text-gray-900 truncate capitalize">
                              {op?.resolver ? op?.resolver?.name : "--"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Closing Date</p>
                            <p className="font-bold text-sm text-gray-900 truncate">
                              {op?.updatedAt
                                ? dateFormater(op?.updatedAt)
                                : "--"}
                            </p>
                          </div>
                        </>
                      )}
                          {!isDashboard && (
                            <Button
                              size="medium"
                              variant="contained"
                              onClick={() => handleTaskView(op)}
                              className="pl-2 pr-2 sm:pl-5 sm:pr-5 w-24"
                            >
                              View Task
                            </Button>
                          )}
                    </div>
                  </div>
                )}
              </>
            );
          })}
        </InfiniteScroll>
      )}
    </>
  );
};

export default PendingTaskTile;
